import { COLS, ROWS, BLOCK_SIZE, KEY } from './js/constants.js';
import { Board } from './js/board.js';
import { Piece } from './js/pieces.js';
import { Scoreboard } from './js/scoreboard.js';

class TetrisGame {
    constructor() {
        this.canvas = document.getElementById('game-board');
        this.ctx = this.canvas.getContext('2d');
        this.board = new Board(this.ctx);
        this.scoreboard = new Scoreboard();
        this.isPlaying = false;
        this.lastTime = 0;
        this.dropCounter = 0;
        this.dropInterval = 8000; // Very slow initial speed

        this.setupCanvas();
        this.setupEventListeners();
        this.updateScore();

        // Initialize scoreboard (starts empty)
        setTimeout(() => {
            this.updateScoreboard();
        }, 100);
    }

    setupCanvas() {
        // Calculate optimal block size for mobile
        const maxWidth = Math.min(window.innerWidth * 0.9, 300);
        const maxHeight = Math.min(window.innerHeight * 0.6, 600);
        const blockSize = Math.min(maxWidth / COLS, maxHeight / ROWS);

        this.ctx.canvas.width = COLS * blockSize;
        this.ctx.canvas.height = ROWS * blockSize;
        this.ctx.scale(blockSize, blockSize);

        // Store block size for reference
        this.blockSize = blockSize;
    }

    setupEventListeners() {
        document.addEventListener('keydown', this.handleKeyPress.bind(this));

        const startButton = document.querySelector('.start-button');
        startButton.addEventListener('click', () => {
            if (this.isPlaying) {
                this.pause();
            } else {
                this.play();
            }
        });

        const clearScoresButton = document.querySelector('#clear-scores');
        if (clearScoresButton) {
            clearScoresButton.addEventListener('click', () => {
                this.scoreboard.clearScores();
                this.updateScoreboard();
                this.updateScore();
            });
        }

        // Scoreboard toggle functionality
        const scoreboardToggle = document.querySelector('#scoreboard-toggle');
        const scoreboardPanel = document.querySelector('#scoreboard-side-panel');
        const closeScoreboard = document.querySelector('#close-scoreboard');

        if (scoreboardToggle) {
            scoreboardToggle.addEventListener('click', () => {
                scoreboardPanel.classList.add('open');
            });
        }

        if (closeScoreboard) {
            closeScoreboard.addEventListener('click', () => {
                scoreboardPanel.classList.remove('open');
            });
        }

        // Debug button removed - scoreboard is working

        // Touch controls for mobile
        this.setupTouchControls();
        this.setupMobileControls();

        // Handle window resize
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    setupTouchControls() {
        let touchStartX = 0;
        let touchStartY = 0;

        this.canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            const touch = e.touches[0];
            touchStartX = touch.clientX;
            touchStartY = touch.clientY;
        });

        this.canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            const touch = e.changedTouches[0];
            const deltaX = touch.clientX - touchStartX;
            const deltaY = touch.clientY - touchStartY;
            const minSwipeDistance = 30;

            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                if (deltaX > minSwipeDistance) {
                    this.board.movePiece(1, 0);
                } else if (deltaX < -minSwipeDistance) {
                    this.board.movePiece(-1, 0);
                }
            } else {
                if (deltaY > minSwipeDistance) {
                    this.board.hardDrop();
                } else if (deltaY < -minSwipeDistance) {
                    this.board.rotatePiece();
                }
            }
        });
    }

    setupMobileControls() {
        const controlButtons = document.querySelectorAll('.control-btn');

        controlButtons.forEach(button => {
            button.addEventListener('touchstart', (e) => {
                e.preventDefault();
                const action = button.dataset.action;
                this.handleMobileAction(action);
            });

            button.addEventListener('click', (e) => {
                e.preventDefault();
                const action = button.dataset.action;
                this.handleMobileAction(action);
            });
        });
    }

    handleMobileAction(action) {
        if (!this.isPlaying) return;

        switch (action) {
            case 'left':
                this.board.movePiece(-1, 0);
                break;
            case 'right':
                this.board.movePiece(1, 0);
                break;
            case 'down':
                this.board.movePiece(0, 1);
                break;
            case 'rotate':
                this.board.rotatePiece();
                break;
            case 'hardDrop':
                this.board.hardDrop();
                break;
        }
    }

    handleKeyPress(event) {
        if (!this.isPlaying) return;

        switch (event.code) {
            case KEY.LEFT:
                event.preventDefault();
                this.board.movePiece(-1, 0);
                break;
            case KEY.RIGHT:
                event.preventDefault();
                this.board.movePiece(1, 0);
                break;
            case KEY.DOWN:
                event.preventDefault();
                this.board.movePiece(0, 1);
                break;
            case KEY.UP:
                event.preventDefault();
                this.board.rotatePiece();
                break;
            case KEY.SPACE:
                event.preventDefault();
                this.board.hardDrop();
                break;
            case KEY.ROTATE:
                event.preventDefault();
                this.board.rotatePiece();
                break;
        }
    }

    play() {
        this.isPlaying = true;
        this.board.reset();
        this.spawnPiece();
        this.gameLoop();
        document.querySelector('.start-button').textContent = 'Pause';
    }

    pause() {
        this.isPlaying = false;
        document.querySelector('.start-button').textContent = 'Resume';
    }

    spawnPiece() {
        this.board.piece = new Piece(this.ctx);
        if (!this.board.valid(this.board.piece)) {
            this.gameOver();
        }
    }

    gameOver() {
        this.isPlaying = false;
        document.querySelector('.start-button').textContent = 'Game Over - Restart';
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        this.ctx.fillRect(0, 0, COLS, ROWS);

        this.ctx.fillStyle = '#fff';
        this.ctx.font = '1px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.fillText('GAME OVER', COLS / 2, ROWS / 2 - 1);
        this.ctx.fillText(`Score: ${this.board.score}`, COLS / 2, ROWS / 2);

        // Check if score qualifies for scoreboard
        if (this.scoreboard.isTopScore(this.board.score)) {
            this.showUsernameModal();
        }
    }

    gameLoop(currentTime = 0) {
        if (!this.isPlaying) return;

        const deltaTime = currentTime - this.lastTime;
        this.lastTime = currentTime;

        this.dropCounter += deltaTime;
        if (this.dropCounter > this.dropInterval) {
            if (!this.board.drop()) {
                this.spawnPiece();
            }
            this.dropCounter = 0;
        }

        // Update board and spawn new piece if needed
        if (!this.board.update(this.dropInterval)) {
            this.spawnPiece();
        }

        this.draw();
        this.updateScore();

        requestAnimationFrame(this.gameLoop.bind(this));
    }

    // update() method removed - now handled in gameLoop

    draw() {
        // Clear the entire canvas
        this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);

        // Draw the board (placed pieces)
        this.board.drawBoard();

        // Draw the current falling piece
        if (this.board.piece) {
            this.board.piece.draw();
        }
    }

    updateScore() {
        const scoreElement = document.querySelector('.score');
        const linesElement = document.querySelector('.lines');
        const levelElement = document.querySelector('.level');
        const bestScoreElement = document.querySelector('.best-score');

        if (scoreElement) scoreElement.textContent = this.board.score;
        if (linesElement) linesElement.textContent = this.board.lines;
        if (levelElement) levelElement.textContent = this.board.level;
        if (bestScoreElement) bestScoreElement.textContent = this.scoreboard.getTopScore();

        // Debug logging (only when values change)
        if (this.board.lines > 0 || this.board.score > 0) {
            console.log(`Score: ${this.board.score}, Lines: ${this.board.lines}, Level: ${this.board.level}`);
        }
    }

    updateScoreboard() {
        let scoreboardList = document.getElementById('scoreboard-list');
        console.log('Updating scoreboard, element found:', scoreboardList);

        // If scoreboard elements don't exist, create them
        if (!scoreboardList) {
            console.log('Creating scoreboard elements...');
            this.createScoreboardElements();
            scoreboardList = document.getElementById('scoreboard-list');
        }

        if (!scoreboardList) {
            console.error('Scoreboard list element not found!');
            return;
        }

        const scores = this.scoreboard.getScores();
        console.log('Scores to display:', scores);

        scoreboardList.innerHTML = '';

        if (scores.length === 0) {
            scoreboardList.innerHTML = '<div class="scoreboard-item"><div class="scoreboard-username" style="grid-column: 1 / -1; text-align: center; opacity: 0.6;">No scores yet</div></div>';
            console.log('No scores to display');
            return;
        }

        scores.forEach((score, index) => {
            const scoreItem = document.createElement('div');
            scoreItem.className = 'scoreboard-item';

            // Show username only for top 5, show "---" for others
            const username = index < 5 ? score.username : '---';

            scoreItem.innerHTML = `
                <div class="scoreboard-rank">${index + 1}</div>
                <div class="scoreboard-username">${username}</div>
                <div class="scoreboard-score">${this.scoreboard.formatScore(score.score)}</div>
            `;
            scoreboardList.appendChild(scoreItem);
        });

        console.log('Scoreboard updated with', scores.length, 'scores');
    }

    createScoreboardElements() {
        // Find the game panel
        const gamePanel = document.querySelector('.game-panel');
        if (!gamePanel) {
            console.error('Game panel not found!');
            return;
        }

        // Create scoreboard section
        const scoreboardSection = document.createElement('div');
        scoreboardSection.className = 'scoreboard-section';
        scoreboardSection.innerHTML = `
            <h3>🏆 Top Scores</h3>
            <div class="scoreboard-container">
                <div class="scoreboard-list" id="scoreboard-list">
                    <!-- Scores will be populated here -->
                </div>
            </div>
        `;

        // Insert at the beginning of the game panel
        gamePanel.insertBefore(scoreboardSection, gamePanel.firstChild);
        console.log('Scoreboard elements created');
    }

    showUsernameModal() {
        const modal = document.createElement('div');
        modal.className = 'modal-overlay';
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-title">New High Score!</div>
                <div class="modal-message">Your score of ${this.scoreboard.formatScore(this.board.score)} qualifies for the scoreboard!</div>
                <input type="text" class="username-input" placeholder="ENTER NAME" maxlength="7" autocomplete="off">
                <div class="modal-buttons">
                    <button class="modal-btn" id="save-score">Save Score</button>
                    <button class="modal-btn cancel" id="cancel-score">Skip</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        const input = modal.querySelector('.username-input');
        const saveBtn = modal.querySelector('#save-score');
        const cancelBtn = modal.querySelector('#cancel-score');

        input.focus();
        input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                saveScore();
            }
        });

        const saveScore = () => {
            const username = input.value.trim();
            console.log('Saving score:', username, this.board.score);
            const rank = this.scoreboard.addScore(username, this.board.score, this.board.level, this.board.lines);
            console.log('Score saved at rank:', rank);
            this.updateScoreboard();
            this.updateScore();
            document.body.removeChild(modal);
        };

        const cancelScore = () => {
            document.body.removeChild(modal);
        };

        saveBtn.addEventListener('click', saveScore);
        cancelBtn.addEventListener('click', cancelScore);
    }

    handleResize() {
        this.setupCanvas();
    }
}

// Initialize the game when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new TetrisGame();
});